import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  IPublicClientApplication,
} from '@azure/msal-browser';
import { defer, Observable, throwError } from 'rxjs';
import {
  catchError,
  finalize,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs/operators';

import { environment } from '../../environments/environment';
import {
  ApiResponse,
  AuthConfig,
  LoginData,
  UserInfo,
} from '../types/auth.types';
import { Role, ROLES } from '../types/roles';
import {
  enVisorPublico,
  rutaActualDelNavegador,
} from '../utils/public-route.util';
import {
  consumirRutaDestino,
  esRutaDestinoValida,
  leerRutaDestino,
} from '../utils/return-url.util';
import {
  normalizeRole,
  normalizeRoles,
} from '../utils/role-normalization.util';

/**
 * Servicio que gestiona la autenticación con Azure AD (MSAL) y el consumo de la API
 * para manejar tokens JWT (Access y Refresh).
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);

  /**
   * URL base de la API para autenticación
   */
  private readonly apiAuthUrl = environment.apiAuthUrl;

  /**
   * ID del sistema para autenticación.
   */
  private readonly systemId = environment.systemId;

  /**
   * Clave de almacenamiento local para la información del usuario
   */
  private readonly userInfoKey = 'userInfo';

  /**
   * Clave de almacenamiento local para el Access Token
   */
  private readonly accessTokenKey = 'jwtToken';

  /**
   * Clave de almacenamiento local para el Refresh Token
   */
  private readonly refreshTokenKey = 'jwtRefreshToken';

  /**
   * Configuración específica para MSAL (Azure AD)
   */
  private readonly config: AuthConfig = {
    clientId: environment.azure.clientId,
    authority: `https://login.microsoftonline.com/${environment.azure.tenantId}`,
    redirectUri: environment.azure.redirectUri,
    scopes: environment.azure.scopes,
  };

  /**
   * Instancia principal de la librería MSAL.
   *
   * Es la instancia compartida que provee `MSAL_INSTANCE` (ver
   * `MSALInstanceFactory` en `app.config.ts`). Este servicio ya no crea una
   * propia: dos instancias con el mismo `clientId` compiten por las mismas
   * claves de caché y esa carrera terminaba sacando al usuario de la pantalla
   * en que estaba.
   */
  private readonly msalInstance: IPublicClientApplication =
    inject(MsalService).instance;

  /**
   * Bandera que indica si MSAL ya ha sido inicializado
   */
  private initialized = false;

  /**
   * Observable compartido para evitar múltiples llamados concurrentes al refresh.
   * Si existe, las peticiones concurrentes se suscriben al mismo flujo.
   */
  private refreshInFlight$: Observable<string> | null = null;

  /**
   * Signal que contiene la información de la cuenta autenticada en MSAL
   */
  public user = signal<AccountInfo | null>(null);

  /**
   * Signal que contiene los datos principales del usuario (nombre, email, roles)
   * provenientes de nuestra API/Backend
   */
  public userInfo = signal<UserInfo | null>(null);

  /**
   * Signal que contiene la lista de roles del usuario (provenientes de la API)
   */
  private readonly userRoles = signal<string[]>([]);

  // ─── Dev: override de roles ────────────────────────────────────────────────
  /** Clave en sessionStorage para el override de roles de desarrollo */
  private readonly devRoleOverrideKey = '__dev_roles_override__';

  /**
   * Signal con los roles de desarrollo sobreescritos.
   * `null` = sin override activo (se usan los roles reales).
   * Solo aplica cuando `environmentName === 'Local'`.
   */
  public readonly devRoleOverride = signal<string[] | null>(null);

  /**
   * Roles disponibles en el sistema (referencia rápida para la UI).
   * Solo se expone en ambientes no productivos.
   */
  public readonly availableRoles: readonly Role[] = Object.values(ROLES);

  // ─── Dev: override de órgano ───────────────────────────────────────────────
  /** Clave en sessionStorage para el override de órgano de desarrollo */
  private readonly devOrganoOverrideKey = '__dev_organo_override__';

  /**
   * Signal con el id_organo sobreescrito para pruebas.
   * `null` = sin override (se usa el órgano real del usuario).
   * Solo aplica cuando `environmentName === 'Local'`.
   */
  public readonly devOrganoOverride = signal<number | null>(null);
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * Signal que contiene el mensaje de error de autenticación
   */
  public authError = signal<string | null>(null);

  /**
   * Signal que contiene el indicador de iniciando sesión
   */
  public isSigningIn = signal<boolean>(false);

  /**
   * Constructor del AuthService
   * @param router Enrutador de Angular para redireccionar según estados de auth
   * @param http Cliente HTTP para comunicarse con la API
   */
  constructor() {
    this.restoreDevRoleOverride();

    // Inicializa MSAL y luego llama a initializeAuth()
    this.msalInstance.initialize().then(() => {
      this.initialized = true;

      // En el visor público no hay sesión que reconstruir. Además,
      // `handleRedirectPromise()` borra el hash de la URL cuando cree estar
      // procesando una respuesta de Azure, y con `withHashLocation()` eso
      // destruye la ruta `#/viewer/...`: el router acaba en `''`, que redirige
      // al área protegida, y el guard expulsa al login. Ver
      // `specs/SPEC-AC-005-aislamiento-visor-publico.md`.
      if (enVisorPublico()) {
        return;
      }

      this.initializeAuth();
    });
  }

  /**
   * Navega salvo que el usuario esté en el visor público.
   *
   * El visor es la única pantalla que vive fuera de la sesión: cualquier salto
   * al login o al área protegida desde ahí es un falso positivo, porque no hay
   * sesión que recuperar ni pantalla protegida que custodiar.
   *
   * @param comandos Comandos de ruta para el `Router`
   */
  private navegarSalvoEnVisorPublico(comandos: string[]): void {
    if (enVisorPublico()) {
      return;
    }

    this.router.navigate(comandos);
  }

  /**
   * Indica si la pantalla actual se sirve sin sesión: el visor público o la
   * propia pantalla de login.
   *
   * Lee la URL del navegador y no `Router.url` de forma deliberada: esta
   * comprobación corre durante el arranque, cuando el router todavía no ha
   * resuelto la primera navegación y `router.url` vale `/`. Con la lectura
   * anterior la protección fallaba justo cuando hacía falta y el usuario
   * acababa en el login viniendo del visor.
   *
   * @returns true si no procede expulsar al usuario al login
   */
  private enRutaSinSesion(): boolean {
    return enVisorPublico() || rutaActualDelNavegador().startsWith('/auth/');
  }

  /**
   * Navega a la pantalla que corresponde una vez completado el inicio de sesión.
   *
   * Si el usuario llegó por un deep link y el `authGuard` lo desvió al login,
   * ese destino se recupera aquí y se le devuelve; si no había ninguno —entró
   * por la pantalla de login sin más— aterriza en el inicio, como siempre.
   *
   * Se usa `navigateByUrl` porque el destino es una URL completa que puede
   * llevar query params, no una lista de segmentos.
   *
   * Ver `specs/SPEC-AC-007-return-url-deep-links-autenticados.md`.
   */
  private navegarADestinoTrasLogin(destinoPreferido?: string | null): void {
    if (enVisorPublico()) {
      return;
    }

    // El destino que vuelve de Azure AD manda sobre el almacenado: pertenece a
    // esta transacción de login concreta. Aun así se valida, porque llega de
    // fuera. El almacenado se consume igualmente para no dejarlo pendiente.
    const almacenado = consumirRutaDestino();
    const destino = esRutaDestinoValida(destinoPreferido)
      ? (destinoPreferido as string)
      : almacenado;

    if (destino) {
      this.router.navigateByUrl(destino);
      return;
    }

    this.router.navigate(['/app/home']);
  }

  /**
   * Realiza las tareas iniciales de autenticación:
   * - Maneja la promesa de redirección de MSAL
   * - Verifica si ya hay cuentas en sesión
   * - Redirecciona a /auth/login si el usuario no está autenticado
   * @private
   */
  private initializeAuth(): void {
    defer(() => this.msalInstance.handleRedirectPromise())
      .pipe(
        tap((result) => {
          if (result) {
            // Si tenemos un resultado de autenticación tras un redirect exitoso
            this.handleAuthenticationSuccess(result);
          } else {
            // Si no hay un resultado de redirección, revisamos si hay cuentas
            const accounts = this.msalInstance.getAllAccounts();
            if (accounts.length > 0) {
              // Si ya hay cuenta en MSAL, la establecemos
              this.user.set(accounts[0]);
              this.tapUserInfo();

              // Si la URL actual es la de login, salimos hacia el destino
              // pendiente (o a home, si no hay ninguno)
              if (this.router.url.includes('/auth/login')) {
                this.navegarADestinoTrasLogin();
              }
            } else {
              // Si no hay cuentas, solo redirigimos a login si no estamos en
              // una pantalla que se sirve sin sesión.
              if (!this.enRutaSinSesion()) {
                this.router.navigate(['/auth/login']);
              }
            }
          }
        }),
        catchError((error) => {
          console.error('Error durante la autenticación:', error);

          // Solo redirigir a login si no estamos en rutas públicas
          if (!this.enRutaSinSesion()) {
            this.router.navigate(['/auth/login']);
          }

          return defer(async () => null);
        }),
      )
      .subscribe();
  }

  /**
   * Dispara el proceso de inicio de sesión usando redirección de Azure AD (MSAL).
   * @returns Observable<void>
   */
  public login(): Observable<void> {
    if (!this.initialized) {
      return throwError(() => new Error('MSAL no se ha inicializado aún'));
    }

    // El destino pendiente viaja también en el `state` de la petición: Azure AD
    // lo devuelve intacto en la respuesta, así que el enlace sobrevive aunque
    // el almacenamiento de la pestaña no llegue de vuelta (navegador integrado
    // de un cliente de correo, almacenamiento bloqueado). `sessionStorage`
    // queda como respaldo para los flujos que no pasan por aquí.
    // Ver `specs/SPEC-AC-007-return-url-deep-links-autenticados.md`.
    const destinoPendiente = leerRutaDestino();

    // Devolvemos un Observable que, al suscribirse, inicia la redirección
    return defer(() => {
      this.msalInstance.loginRedirect({
        scopes: this.config.scopes,
        ...(destinoPendiente ? { state: destinoPendiente } : {}),
      });
      // Después de llamar a loginRedirect, se interrumpe el flujo para el usuario
      // (la página redirige a Azure AD), pero no queremos que RxJS arroje un error.
      return Promise.resolve();
    });
  }

  /**
   * Cierra la sesión del usuario tanto en nuestra API como en Azure AD con redirección
   * @returns Observable<void>
   */
  public logout(): Observable<void> {
    // Construimos el payload requerido por el backend
    const info = this.getUserInfo() as unknown as { userId?: string } | null;
    const userId = info?.userId ?? '';
    const systemId = this.systemId;

    // Primero avisa a nuestra API que el usuario cerró sesión
    return this.http
      .post<ApiResponse<string>>(`${this.apiAuthUrl}/auth/logout`, {
        userId,
        systemId,
      })
      .pipe(
        tap({
          next: (resp) => {
            // Aunque el backend reporte error, procedemos con el cierre local
            if (!resp?.isSuccess) {
              console.warn(
                `Logout backend no exitoso: ${resp?.message} (status: ${resp?.statusCode})`,
              );
            }
            // Limpiamos todo lo relacionado a la sesión en localStorage y signals
            this.user.set(null);
            this.userInfo.set(null);
            localStorage.removeItem(this.userInfoKey);
            localStorage.removeItem(this.accessTokenKey);
            localStorage.removeItem(this.refreshTokenKey);
            this.userRoles.set([]);
          },
        }),
        switchMap(() =>
          defer(() => {
            // Llamamos a logoutRedirect para que Azure AD
            // gestione el cierre de sesión con redirección
            this.msalInstance.logoutRedirect();
            // Retornamos un Promise vacío para que el observable no falle
            return Promise.resolve();
          }),
        ),
        catchError((error) => {
          console.error('Error durante el logout:', error);
          // En caso de error, navegamos a /auth/login
          this.navegarSalvoEnVisorPublico(['/auth/login']);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Indica si hay un usuario autenticado en MSAL (cuentas en memoria)
   * @returns true si el usuario está autenticado, false en caso contrario
   */
  public isAuthenticated(): boolean {
    const hasUserInfo = this.getUserInfo() !== null;
    const hasMsalAccounts = this.msalInstance.getAllAccounts().length > 0;
    return hasMsalAccounts && hasUserInfo;
  }

  /**
   * Devuelve la información de usuario guardada en localStorage
   * @returns UserInfo | null - Objeto con la información del usuario o null si no existe o es inválido
   */
  public getUserInfo(): UserInfo | null {
    try {
      const storedData = localStorage.getItem(this.userInfoKey);
      if (!storedData) return null;

      const parsedData = JSON.parse(storedData);

      // Validación básica para asegurar que el objeto cumple con la interfaz UserInfo
      if (typeof parsedData !== 'object' || parsedData === null) return null;

      const userInfo = parsedData as UserInfo;
      const roles = Array.isArray(userInfo.roles) ? userInfo.roles : [];
      const normalizedRoles = normalizeRoles(roles as string[]);
      const changed =
        normalizedRoles.length !== roles.length ||
        normalizedRoles.some((r, i) => r !== (roles as string[])[i]);
      if (changed) {
        const patched: UserInfo = { ...userInfo, roles: normalizedRoles };
        localStorage.setItem(this.userInfoKey, JSON.stringify(patched));
        return patched;
      }
      return { ...userInfo, roles: normalizedRoles };
    } catch (error) {
      console.error('Error al recuperar información de usuario:', error);
      return null;
    }
  }

  /**
   * Guarda un objeto con la información del usuario en localStorage
   * @param userInfo Objeto con información del usuario (ej. nombre, email, roles)
   */
  public setUserInfo(userInfo: unknown): void {
    localStorage.setItem(this.userInfoKey, JSON.stringify(userInfo));
  }

  /**
   * Devuelve el Access Token (JWT) almacenado en localStorage
   * En modo de desarrollo, usa el token del environment si no hay uno en localStorage
   * @returns string | null
   */
  public getAccessToken(): string | null {
    const storedToken = localStorage.getItem(this.accessTokenKey);
    return storedToken;
  }

  /**
   * Guarda el Access Token (JWT) en localStorage
   * @param token Cadena JWT
   */
  public setAccessToken(token: string): void {
    localStorage.setItem(this.accessTokenKey, token);
  }

  /**
   * Devuelve el Refresh Token almacenado
   * @returns string | null
   */
  public getRefreshToken(): string | null {
    return localStorage.getItem(this.refreshTokenKey);
  }

  /**
   * Guarda el Refresh Token en localStorage
   * @param token Cadena del Refresh Token
   */
  public setRefreshToken(token: string): void {
    localStorage.setItem(this.refreshTokenKey, token);
  }

  /**
   * Invoca el endpoint de nuestra API para refrescar el token usando el Refresh Token
   * @param expiredToken JWT expirado
   * @returns Observable<string> con el nuevo Access Token
   */
  public handleRefreshToken(expiredToken: string): Observable<string> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No existe Refresh Token almacenado'));
    }

    // Si ya hay un refresh en curso, reutilizamos el mismo observable compartido
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    const request$ = this.http
      .post<ApiResponse<LoginData>>(`${this.apiAuthUrl}/auth/refresh`, {
        accessToken: expiredToken || '',
        refreshToken,
      })
      .pipe(
        switchMap((response) => {
          if (!response?.isSuccess || !response?.data) {
            const msg =
              response?.message || 'Refresh token inválido o expirado';
            return throwError(() => new Error(msg));
          }
          // Almacenar los tokens
          this.setAccessToken(response.data.token);
          this.setRefreshToken(response.data.refreshToken);
          return defer(() => Promise.resolve(response.data.token));
        }),
        finalize(() => {
          // Al completar o error, liberamos el observable compartido
          this.refreshInFlight$ = null;
        }),
        // Compartimos el resultado entre concurrentes, liberando suscripción al finalizar
        shareReplay({ bufferSize: 1, refCount: true }),
      );

    this.refreshInFlight$ = request$;
    return request$;
  }

  /**
   * Determina si un token está por expirar pronto (por ejemplo, en 30s)
   * @param token JWT a evaluar
   * @returns true si faltan <= 30s para expirar, false de lo contrario
   */
  public isTokenExpiringSoon(token: string): boolean {
    try {
      // Dividimos el token por '.', el segundo índice (payload) está en Base64
      const payload = token.split('.')[1];
      if (!payload) return true;

      // Decodificamos Base64
      const decoded = JSON.parse(atob(payload));
      const exp = decoded.exp; // exp es tiempo UNIX en segundos

      if (!exp) return true;

      const nowInSec = Math.floor(Date.now() / 1000);
      const secondsLeft = exp - nowInSec;
      const REFRESH_MARGIN = 30; // margen de 30 segundos

      return secondsLeft <= REFRESH_MARGIN;
    } catch (e) {
      console.error('Error al decodificar token:', e);
      // Si falla la decodificación, asumimos que el token no es válido
      return true;
    }
  }

  /**
   * Maneja el proceso posterior a una autenticación exitosa con MSAL.
   * - Establece la cuenta en la Signal 'user'
   * - Adquiere el token de Azure en modo silencioso
   * - Envía el token de Azure a nuestra API para obtener un JWT propio
   * - Decodifica y almacena roles, email y nombre de usuario
   * @param result Objeto con datos del resultado de la autenticación
   * @private
   */
  private handleAuthenticationSuccess(result: AuthenticationResult): void {
    if (!result.account) {
      return;
    }

    // Guardamos la cuenta autenticada en MSAL
    this.user.set(result.account);

    // Extraemos info básica del token de Azure
    const idTokenClaims = result.idTokenClaims as any;
    const name = idTokenClaims.name || idTokenClaims.given_name || '';
    const email =
      idTokenClaims.preferred_username || idTokenClaims.emails?.[0] || '';

    // Obtenemos un token de Azure (silent) para enviarlo a nuestra API
    defer(() =>
      this.msalInstance.acquireTokenSilent({
        scopes: this.config.scopes,
        account: result.account,
      }),
    )
      .pipe(
        switchMap((tokenResponse) => {
          // Enviamos el token de Azure a la API para que nos devuelva nuestro JWT
          // Limpiamos cualquier error previo
          this.authError.set(null);
          this.isSigningIn.set(true);

          return this.http
            .post<ApiResponse<LoginData>>(`${this.apiAuthUrl}/auth/login`, {
              tipo: 'Ms365',
              token: tokenResponse.accessToken,
              idSistema: this.systemId,
            })
            .pipe(
              tap((response) => {
                if (!response.isSuccess || !response.data) {
                  const errorMessage =
                    response.message ||
                    'Error al validar credenciales durante el inicio de sesión';

                  this.authError.set(errorMessage);
                  this.user.set(null);
                  this.userInfo.set(null);
                  this.userRoles.set([]);
                  this.navegarSalvoEnVisorPublico(['/auth/login']);
                  this.isSigningIn.set(false);
                  throw new Error(errorMessage);
                }

                const { token, refreshToken, profile } = response.data;
                // Almacenar los tokens
                this.setAccessToken(token);
                this.setRefreshToken(refreshToken);

                // Roles: priorizar los roles del perfil de la API; fallback al token de Azure
                let roles: string[] = Array.isArray(profile?.roles)
                  ? (profile!.roles as string[])
                  : [];
                if (!roles || roles.length === 0) {
                  try {
                    const tokenPayload = JSON.parse(
                      atob(result.accessToken.split('.')[1]),
                    );
                    roles = tokenPayload.roles || [];
                  } catch {
                    roles = [];
                  }
                }

                const normalizedRoles = normalizeRoles(roles);

                // Actualizamos roles en nuestra Signal
                this.userRoles.set(normalizedRoles);

                // Información de usuario: priorizar nombre/email del perfil
                const userInfo: UserInfo = {
                  userId:
                    profile?.idUsuario !== undefined
                      ? String(profile.idUsuario)
                      : undefined,
                  name: profile?.nombreUsuario || name,
                  email: profile?.email || email,
                  employeeNumber: profile?.numeroEmpleado
                    ? String(profile.numeroEmpleado)
                    : undefined,
                  roles: normalizedRoles,
                };

                this.setUserInfo(userInfo);
                this.tapUserInfo();

                this.isSigningIn.set(false);

                // Redirigimos al destino pendiente, o a la página de inicio.
                // `result.state` es el destino que adjuntamos al lanzar el
                // login y que Azure AD nos devuelve.
                this.navegarADestinoTrasLogin(result.state);
              }),
              catchError((error) => {
                // Extraemos el mensaje de error de la respuesta
                const errorMessage =
                  'Error al validar token durante el inicio de sesión';

                // Actualizamos el signal de error
                this.authError.set(errorMessage);

                // Limpiamos cualquier información de sesión previa
                this.user.set(null);
                this.userInfo.set(null);
                this.userRoles.set([]);

                // Redirigimos al usuario a la página de login
                this.navegarSalvoEnVisorPublico(['/auth/login']);

                this.isSigningIn.set(false);

                // Propagamos el error
                return throwError(() => new Error(errorMessage));
              }),
            );
        }),
        catchError((error) => {
          console.error('Error durante el proceso de autenticación:', error);
          this.userRoles.set([]);

          return defer(async () => null);
        }),
      )
      .subscribe();
  }

  /**
   * Restaura la información del usuario desde localStorage y actualiza los signals
   * @private
   */
  private tapUserInfo(): void {
    const userInfo = this.getUserInfo();
    if (userInfo) {
      this.userInfo.set(userInfo);
      this.userRoles.set(userInfo.roles ?? []);
    }
  }

  /**
   * Verifica si el usuario posee un rol específico.
   * En desarrollo, si hay un override activo se verifica contra esos roles.
   * @param role Rol a verificar (ej. 'Administrador')
   * @returns true si el usuario tiene ese rol, false de lo contrario
   */
  public hasRole(role: string): boolean {
    const effectiveRoles = this.getEffectiveRoles();
    const required = normalizeRole(role);
    return effectiveRoles.includes(required);
  }

  /**
   * Devuelve los roles efectivos del usuario.
   * En desarrollo, si hay un override activo devuelve esos roles; si no, los reales.
   */
  public getEffectiveRoles(): string[] {
    const override = this.devRoleOverride();
    const raw = override !== null ? override : this.userRoles();
    return normalizeRoles(raw);
  }

  /**
   * Indica si hay un override de roles de desarrollo activo.
   */
  public isDevRoleActive(): boolean {
    return (
      environment.environmentName === 'Local' && this.devRoleOverride() !== null
    );
  }

  // ─── Métodos de dev: cambio de roles ───────────────────────────────────────

  /**
   * Sobreescribe los roles del usuario para pruebas locales.
   * Solo funciona en ambientes NO productivos.
   * @param roles Lista de roles a simular
   */
  public setDevRoles(roles: string[]): void {
    if (environment.environmentName !== 'Local') {
      console.warn(
        '[AuthService] setDevRoles no está disponible en producción.',
      );
      return;
    }
    this.devRoleOverride.set([...roles]);
    sessionStorage.setItem(this.devRoleOverrideKey, JSON.stringify(roles));
  }

  /**
   * Elimina el override de roles y restaura los roles reales del usuario.
   * Solo funciona en ambientes NO productivos.
   */
  public clearDevRoles(): void {
    if (environment.environmentName !== 'Local') return;
    this.devRoleOverride.set(null);
    sessionStorage.removeItem(this.devRoleOverrideKey);
  }

  /**
   * Sobreescribe el órgano del usuario para pruebas locales.
   * Solo funciona en ambientes NO productivos.
   * @param idOrgano ID del órgano a simular, o `null` para limpiar
   */
  public setDevOrgano(idOrgano: number | null): void {
    if (environment.environmentName !== 'Local') {
      console.warn(
        '[AuthService] setDevOrgano no está disponible en producción.',
      );
      return;
    }
    this.devOrganoOverride.set(idOrgano);
    if (idOrgano !== null) {
      sessionStorage.setItem(this.devOrganoOverrideKey, String(idOrgano));
    } else {
      sessionStorage.removeItem(this.devOrganoOverrideKey);
    }
  }

  /**
   * Elimina el override de órgano y restaura el órgano real del usuario.
   * Solo funciona en ambientes NO productivos.
   */
  public clearDevOrgano(): void {
    if (environment.environmentName !== 'Local') return;
    this.devOrganoOverride.set(null);
    sessionStorage.removeItem(this.devOrganoOverrideKey);
  }

  /**
   * Indica si hay un override de órgano de desarrollo activo.
   */
  public isDevOrganoActive(): boolean {
    return (
      environment.environmentName === 'Local' &&
      this.devOrganoOverride() !== null
    );
  }

  /**
   * Restaura los overrides de roles y órgano guardados en sessionStorage al inicializar.
   * Permite que los overrides sobrevivan un F5 durante pruebas.
   * @private
   */
  private restoreDevRoleOverride(): void {
    if (environment.environmentName !== 'Local') return;
    try {
      const storedRoles = sessionStorage.getItem(this.devRoleOverrideKey);
      if (storedRoles) {
        const roles = JSON.parse(storedRoles);
        if (Array.isArray(roles)) {
          this.devRoleOverride.set(roles);
        }
      }
    } catch {
      sessionStorage.removeItem(this.devRoleOverrideKey);
    }
    try {
      const storedOrgano = sessionStorage.getItem(this.devOrganoOverrideKey);
      if (storedOrgano) {
        const id = parseInt(storedOrgano, 10);
        if (!isNaN(id)) {
          this.devOrganoOverride.set(id);
        }
      }
    } catch {
      sessionStorage.removeItem(this.devOrganoOverrideKey);
    }
  }
}
